import React, { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Button, Drawer, Form, Input, InputNumber, Select, Space, Divider } from "antd";
import { DatePicker } from "antd";
import GooglePlacesTagsSelect from "components/common/GooglePlacesTagsSelect";
import useAppConfig from "hooks/access-control/useAppConfig";
import { locationFieldsFromPlaceDetails } from "utils/googlePlaces";
import { csvToArray, enumOptions, formatLocationsForForm, parseLocations } from "./utils";
import {
  UserEducationLevel,
  JobOpportunityWorkSchedule,
} from "./jobEnums";

export default function JobOpportunityDrawer({
  open,
  onClose,
  onSubmit,
  loading,
  initialValues,
  skills = [],
  specializations = [],
}) {
  const [form] = Form.useForm();
  const { appConfig } = useAppConfig();
  const googleMapsApiKey = appConfig?.googleMapsApiKey || appConfig?.googleMapsApiMatrixKey || "";

  const extractIds = (items) =>
    Array.isArray(items)
      ? items
          .map((item) => (typeof item === "string" ? item : item?.id))
          .map(String)
          .filter((val) => val && val !== "undefined" && val !== "null")
      : [];

  const skillOptions = useMemo(
    () =>
      skills.map((item) => ({
        value: String(item.id),
        label: `${item.name || item.code}`,
      })),
    [skills],
  );

  const specializationOptions = useMemo(
    () =>
      specializations.map((item) => ({
        value: String(item.id),
        label: `${item.name || item.code}`,
      })),
    [specializations],
  );

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        postStartDate: initialValues.postStartDate || "",
        postEndDate: initialValues.postEndDate || "",
        otherRequiredSpecializations: csvToArray(initialValues.otherRequiredSpecializations).join(", "),
        otherRequiredWorkAreas: csvToArray(initialValues.otherRequiredWorkAreas).join(", "),
        otherRequiredSkills: csvToArray(initialValues.otherRequiredSkills).join(", "),
	        jobLocations: formatLocationsForForm(initialValues.jobLocations),
        requiredSpecializationIds:
          initialValues.requiredSpecializationIds || extractIds(initialValues.requiredSpecializations),
        requiredSkillIds:
          initialValues.requiredSkillIds || extractIds(initialValues.requiredSkills),
        workSchedules: Array.isArray(initialValues.workSchedules) && initialValues.workSchedules.length
          ? initialValues.workSchedules[0]
          : JobOpportunityWorkSchedule.ANY,
      });
    } else {
      form.setFieldsValue({
	        jobLocations: [],
        workSchedules: JobOpportunityWorkSchedule.ANY,
      });
    }
  }, [open, form, initialValues]);

  const handleFinish = async (values) => {
    const payload = {
      ...values,
      otherRequiredSpecializations: csvToArray(values.otherRequiredSpecializations),
      otherRequiredWorkAreas: csvToArray(values.otherRequiredWorkAreas),
      otherRequiredSkills: csvToArray(values.otherRequiredSkills),
	      jobLocations: parseLocations(values.jobLocations),
      requiredYearsOfExperience:
        values.requiredYearsOfExperience === "" || values.requiredYearsOfExperience === null
          ? undefined
          : values.requiredYearsOfExperience,
      requiredSpecializationIds: values.requiredSpecializationIds || [],
      requiredSkillIds: values.requiredSkillIds || [],
      workSchedules: values.workSchedules ? [values.workSchedules] : [],
    };
    await onSubmit(payload);
  };

  return (
    <Drawer
      title={initialValues?.id ? "Edit Job Post" : "Post a Job"}
      width={920}
      open={open}
      onClose={onClose}
      destroyOnClose
      placement="right"
      zIndex={2000}
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={() => form.submit()}>
            {initialValues?.id ? "Update" : "Publish"}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="e.g. Senior Caregiver, House Cleaner, Tutor..." />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={4} placeholder="Describe the role, responsibilities, and ideal candidate..." />
        </Form.Item>

        <Divider orientation="left" style={{ fontSize: 13, fontWeight: 600 }}>Dates</Divider>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item
            name="postStartDate"
            label="Post Start Date"
            getValueProps={(value) => ({
              value: value ? dayjs(String(value)) : null,
            })}
            getValueFromEvent={(value) => (value ? value.toISOString() : "")}
          >
            <DatePicker showTime format="MMM D, YYYY hh:mm A" style={{ width: "100%" }} placeholder="Select start date" />
          </Form.Item>
          <Form.Item
            name="postEndDate"
            label="Post End Date"
            getValueProps={(value) => ({
              value: value ? dayjs(String(value)) : null,
            })}
            getValueFromEvent={(value) => (value ? value.toISOString() : "")}
          >
            <DatePicker showTime format="MMM D, YYYY hh:mm A" style={{ width: "100%" }} placeholder="Select end date" />
          </Form.Item>
        </div>

        <Form.Item name="workSchedules" label="Job Type">
          <Select options={enumOptions(JobOpportunityWorkSchedule)} allowClear />
        </Form.Item>
	        <Form.Item
	          name="jobLocations"
	          label="Locations"
	        >
          <GooglePlacesTagsSelect
            googleMapsApiKey={googleMapsApiKey}
            placeholder="Search address"
            types={["geocode", "establishment"]}
            tokenSeparators={[]}
            resolveValueFromDetails={(details) => {
              const fields = locationFieldsFromPlaceDetails(details);
              const parts = [fields.city, fields.state, fields.country].filter(Boolean);
              return parts.length ? parts.join(", ") : null;
            }}
          />
        </Form.Item>

        <Divider orientation="left" style={{ fontSize: 13, fontWeight: 600 }}>Candidate Requirements</Divider>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item name="requiredEducationLevel" label="Education Level">
            <Select options={enumOptions(UserEducationLevel)} allowClear />
          </Form.Item>
          <Form.Item name="requiredYearsOfExperience" label="Years of Experience">
            <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 2" />
          </Form.Item>
        </div>

        <Form.Item name="requiredSpecializationIds" label="Specializations / Fields of Study">
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            options={specializationOptions}
            placeholder="Search and select specializations"
          />
        </Form.Item>
        <Form.Item name="otherRequiredSpecializations" label="Other Specializations">
          <Input placeholder="e.g. Nursing, Psychology, Computer Science..." />
        </Form.Item>

        <Divider orientation="left" style={{ fontSize: 13, fontWeight: 600 }}>Skills</Divider>
        <Form.Item name="requiredSkillIds" label="Required Skills">
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={skillOptions} placeholder="Search and select skills" />
        </Form.Item>
        <Form.Item name="otherRequiredSkills" label="Other Skills">
          <Input placeholder="e.g. Communication, Problem solving, Time management..." />
        </Form.Item>

        <Divider orientation="left" style={{ fontSize: 13, fontWeight: 600 }}>Work Areas</Divider>
        <Form.Item name="otherRequiredWorkAreas" label="Work Areas">
          <Input placeholder="e.g. Home care, Tutoring, Cleaning, Moving..." />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
